import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import { getDataView } from "@insite/client-framework/Store/Data/DataState";
import { loadPagesForParent } from "@insite/client-framework/Store/Data/Pages/PagesActionCreators";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import updateLoadParameter from "@insite/client-framework/Store/Pages/NewsList/Handlers/UpdateLoadParameter";
import translate from "@insite/client-framework/Translate";
import { FacetModel } from "@insite/client-framework/Types/ApiModels";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import Button from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Pagination, { PaginationPresentationProps } from "@insite/mobius/Pagination";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import convertToNumberIfString from "@insite/mobius/utilities/convertToNumberIfString";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CarosulArrow from "@tIntermatic/Icons/CarosulArrow";
import addNewsFilters from "@tIntermatic/Store/Components/NewsListFilter/Handlers/AddNewsListFilter";
import loadNewsFilters from "@tIntermatic/Store/Components/NewsListFilter/Handlers/LoadNewsListFilters";
import removeAllNewsFilters from "@tIntermatic/Store/Components/NewsListFilter/Handlers/RemoveAllNewsListFilters";
import removeNewsFilters from "@tIntermatic/Store/Components/NewsListFilter/Handlers/RemoveNewsListFilters";
import { getNewsFilters } from "@tIntermatic/Store/Components/NewsListFilter/NewsListFilterSelector";
import CustomApplicationState from "@tIntermatic/Store/CustomApplicationState";
import FilterTypeAccordionSection from "@tIntermatic/Widgets/FilterTypeAccordionSection";
import parse from "html-react-parser";
import sortBy from "lodash/sortBy";
import truncate from "lodash/truncate";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    defaultPageSize = "defaultPageSize",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.defaultPageSize]: number;
    };
}

interface State extends CustomApplicationState {
    isFiltered: boolean;
    marketsData: FacetModel[];
    solutionsData: FacetModel[];
    typesData: FacetModel[];
    newsData: PageProps[];
}

const mapStateToProps = (state: CustomApplicationState) => {
    const pages = getDataView(state.data.pages, { parentNodeId: getCurrentPage(state).nodeId });
    let links: SafeDictionary<PageLinkModel> = {};
    let marketsList: FacetModel[] = [];
    let solutionsList: FacetModel[] = [];
    let typesList: FacetModel[] = [];
    if (pages.value) {
        links = pages.value.reduce(
            (links, page) => ({ ...links, [page.nodeId]: getPageLinkByNodeId(state, page.nodeId) }),
            {},
        );
        const markets = pages.value.map(cat => cat.fields.market);
        const marketsListFiltered = markets.filter(function (elem, pos) {
            // eslint-disable-next-line eqeqeq
            return markets.indexOf(elem) == pos;
        });
        marketsList = marketsListFiltered.map<FacetModel>(o => ({
            ...o,
            id: o,
            name: o,
        }));
        const solutions = pages.value.map(cat => cat.fields.solution);
        const solutionsListFiltered = solutions.filter(function (elem, pos) {
            // eslint-disable-next-line eqeqeq
            return solutions.indexOf(elem) == pos;
        });
        solutionsList = solutionsListFiltered.map<FacetModel>(o => ({
            ...o,
            id: o,
            name: o,
        }));
        const types = pages.value.map(cat => cat.fields.type);
        const typesListFiltered = types.filter(function (elem, pos) {
            // eslint-disable-next-line eqeqeq
            return types.indexOf(elem) == pos;
        });
        typesList = typesListFiltered.map<FacetModel>(o => ({
            ...o,
            id: o,
            name: o,
        }));
    }

    return {
        parentNodeId: getCurrentPage(state).nodeId,
        location: getLocation(state),
        pages,
        links,
        parameter: state.pages.newsList.getNewsPagesParameters[getCurrentPage(state).nodeId] ?? {
            parentNodeId: getCurrentPage(state).nodeId,
        },
        marketsList,
        solutionsList,
        typesList,
        newsListFilter: state.t_components.newsListFilter,
    };
};

const mapDispatchToProps = {
    loadPagesForParent,
    updateLoadParameter,
    addNewsFilters,
    removeNewsFilters,
    getNewsFilters,
    loadNewsFilters,
    removeAllNewsFilters,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface NewsListViewStyles {
    titleText?: TypographyProps;
    authorText?: TypographyProps;
    publishDateText?: TypographyProps;
    listWrapper?: InjectableCss;
    newsWrapper?: InjectableCss;
    summaryWrapper?: InjectableCss;
    dateAndAuthorWrapper?: InjectableCss;
    readMoreLink?: LinkPresentationProps;
    pagination?: PaginationPresentationProps;
    container?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;

    ResourceContainer?: GridContainerProps;
    ResourceListContainer?: GridItemProps;
    ResourceListItemContainer?: GridItemProps;
    Rlptitle?: InjectableCss;
    subResourceNameLinkItem?: GridItemProps;
    innerContainer?: GridContainerProps;
    subResourceImageItem?: GridItemProps;
    subResourceImageClickable?: ClickablePresentationProps;
    subResourceImage?: LazyImageProps;
    subResourceNameLinkItem2?: GridItemProps;
    subResourceNameLink?: LinkPresentationProps;
    PLPheaderText?: TypographyPresentationProps;
    PLPdescriptionsText?: TypographyPresentationProps;
    buttonWrapper?: InjectableCss;
}

export const newsListViewStyles: NewsListViewStyles = {
    listWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            height: auto;
            overflow: hidden;
            padding-bottom: 10px;
        `,
    },
    container: {
        gap: 20,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin: 40px 30px 40px;
                        padding: 0 30px;
                    `,
                    css`
                        margin: 40px 30px 40px;
                    `,
                    css`
                        margin: 58px 0px 20px 0;
                    `,
                    css`
                        margin: 58px 40px 20px 0;
                    `,
                    css`
                        margin: 58px 40px 20px 0;
                    `,
                ])}
        `,
    },
    leftColumnGridItem: {
        width: [12, 12, 3, 3, 3],
    },
    rightColumnGridItem: {
        width: [12, 12, 9, 9, 9],
    },
    titleText: {
        size: 20,
        css: css`
            margin-bottom: 0;
        `,
    },
    newsWrapper: {
        css: css`
            margin-bottom: 15px;
            border-top: 1px solid ${getColor("common.border")};
            padding-top: 15px;
        `,
    },
    pagination: {
        cssOverrides: {
            perPageSelect: css`
                display: none;
                ${({ theme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                display: none;
                            `,
                            css`
                                display: none;
                            `,
                            css`
                                display: none;
                            `,
                            css`
                                display: none;
                            `,
                            css`
                                display: none;
                            `,
                        ],
                        "max",
                    )};
            `,
            pagination: css`
                margin: 0px !important;
                > nav {
                    > ul {
                        > li {
                            button[aria-current="page"] {
                                span {
                                    color: rgb(15, 15, 15) !important;
                                }
                            }
                            button[data-test-selector="pagination_firstButton"],
                            [data-test-selector="pagination_lastButton"] {
                                display: none;
                            }
                            button[data-test-selector="pagination_previousButton"],
                            [data-test-selector="pagination_nextButton"] {
                                margin: 0px;
                                width: 35px;
                                padding: 0px !important;
                                border: none !important;
                            }
                            &:nth-last-child(2) {
                                margin: 0px;
                                width: 22px;
                            }
                            button[data-test-selector="pagination_previousButton"] {
                                > span {
                                    transform: rotate(180deg);
                                }
                            }
                        }
                    }
                }
            `,

            currentButton: css`
                border: 1px solid #cccccc;
                background-color: #ffffff;
                color: #33333a;
                font-size: 18px;
                font-weight: 600;
                letter-spacing: 0;
                line-height: 22px;
                padding: 7px;
                padding-right: 10px;
                &:hover {
                    background-color: #ffffff !important;
                    color: #33333a !important;
                }
            `,
        },
        navIconsSrc: {
            previousPage: CarosulArrow,
            nextPage: CarosulArrow,
        },
        buttonProps: {
            css: css`
                border: none;
                cursor: pointer;
                &:hover {
                    background-color: #ffffff !important;
                    color: #33333a !important;
                }
            `,
        },
    },
    summaryWrapper: {
        css: css`
            margin-top: 8px;
        `,
    },
    authorText: {
        css: css`
            font-weight: bold;
            margin-right: 8px;
        `,
    },
    publishDateText: {
        css: css`
            font-style: italic;
        `,
    },
    ResourceListContainer: {
        width: [12, 12, 12, 3, 3],
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin: 40px 10px 20px 0;
                    `,
                    css`
                        padding: 0;
                    `,
                    css`
                        padding: 0 10px;
                    `,
                    css`
                        padding: 0;
                    `,
                    css`
                        padding: 0;
                    `,
                ])}
        `,
    },
    Rlptitle: {
        css: css`
            font-size: 21px;
            font-weight: bold;
            letter-spacing: 1.75px;
            line-height: 25px;
            color: #33333a;
            margin-left: 7px;
            text-transform: uppercase;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css``,
                        css``,
                        css`
                            font-size: 21px;
                            font-weight: bold;
                            letter-spacing: 1.75px;
                            line-height: 25px;
                            color: #33333a;
                            margin-left: 7px;
                        `,
                        css`
                            font-size: 14px;
                            font-weight: bold;
                            letter-spacing: 1.75px;
                            line-height: 25px;
                            color: #33333a;
                            margin-left: 7px;
                        `,
                        css`
                            font-size: 19px;
                            font-weight: bold;
                            letter-spacing: 1.75px;
                            line-height: 25px;
                            color: #33333a;
                            margin-left: 7px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    subResourceNameLinkItem: {
        width: 12,
        css: css`
            padding: 0 !important;
        `,
    },
    ResourceListItemContainer: {
        width: [12, 12, 12, 9, 9],
        css: css`
            background-color: #ffffff;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin: 10px 0px 40px !important;
                        padding: 0 30px;
                    `,
                    css`
                        margin: 10px 0px 40px !important;
                        padding: 0 30px;
                    `,
                    css`
                        margin: 10px 0px 40px !important;
                    `,
                    css`
                        margin-top: -63px !important;
                        margin-bottom: 29px !important;
                    `,
                    css`
                        margin-top: -63px !important;
                        margin-bottom: 29px !important;
                    `,
                ])}
        `,
    },

    ResourceContainer: {
        gap: 0,
        css: css`
            width: 100%;
            max-width: 1140px;
            margin: 0 auto;
        `,
    },
    innerContainer: {
        gap: 0,
        css: css`
            border: 1px solid #e1e1e1;
            background-color: #ffffff;
            margin: 0 0px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin: 0px;
                            margin-bottom: 30px;
                        `,
                        css`
                            margin: 0px;
                            margin-bottom: 30px;
                        `,
                        css`
                            border: 1px solid #e1e1e1;
                            background-color: #ffffff;
                            margin: 0px;
                            margin-bottom: 30px;
                        `,
                        css`
                            border: 1px solid #e1e1e1;
                            background-color: #ffffff;
                            margin: 0 0px;
                            margin-bottom: 30px;
                        `,
                        css`
                            border: 1px solid #e1e1e1;
                            background-color: #ffffff;
                            margin: 0 0px;
                            margin-bottom: 30px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    subResourceImageItem: {
        width: [12, 12, 12, 4, 4],
        css: css`
            width: 100%;
            padding: 0px;
            display: flex;
            align-items: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 20.5px;
                        `,
                        css`
                            padding: 20.5px;
                        `,
                        css`
                            padding: 20.5px;
                        `,
                        css`
                            padding: 0;
                        `,
                        css`
                            padding: 0;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    subResourceImageClickable: {
        css: css`
            width: 100%;
        `,
    },
    subResourceImage: {
        css: css`
            height: auto;
            width: 100%;
            img {
                height: 100%;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            height: 198px;
                            img {
                                height: auto;
                            }
                        `,
                        css`
                            height: 198px;
                            img {
                                height: auto;
                            }
                        `,
                        css`
                            height: 198px;
                            img {
                                height: auto;
                            }
                        `,
                        css`
                            height: auto;
                            img {
                                height: 100%;
                            }
                        `,
                        css`
                            height: auto;
                            img {
                                height: 100%;
                            }
                        `,
                    ],
                    "min",
                )}
        `,
    },
    subResourceNameLinkItem2: {
        width: [12, 12, 12, 8, 8],
        css: css`
            padding-left: 51px;
            padding-right: 48.5px;
            padding-top: 15px;
            padding-bottom: 15px;
            span {
                text-align: left;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        justify-content: flex-start;
                        align-items: center;
                        padding-left: 20.5px;
                        padding-right: 20.5px;
                        margin-bottom: 25.5px !important;
                        padding-top: 15px;
                        padding-bottom: 0px;
                    `,
                    css`
                        justify-content: flex-start;
                        align-items: center;
                        padding-left: 20.5px;
                        padding-right: 20.5px;
                        margin-bottom: 25.5px !important;
                        padding-top: 15px;
                        padding-bottom: 0px;
                    `,
                    css`
                        justify-content: flex-start;
                        align-items: center;
                        padding-left: 20.5px;
                        padding-right: 20.5px;
                        margin-bottom: 25.5px !important;
                        padding-top: 15px;
                        padding-bottom: 0px;
                    `,
                    css`
                        justify-content: flex-start;
                        align-items: center;
                        padding-left: 51px;
                        padding-right: 48.5px;
                        margin-bottom: 0px !important;
                        padding-top: 15px;
                        padding-bottom: 15px;
                    `,
                    css`
                        justify-content: flex-start;
                        align-items: center;
                        padding-left: 51px;
                        padding-right: 48.5px;
                        margin-bottom: 0px !important;
                        padding-top: 15px;
                        padding-bottom: 15px;
                    `,
                ])}
        `,
    },
    PLPheaderText: {
        css: css`
            font-size: 30px;
            font-weight: 500;
            letter-spacing: 0;
            line-height: 36px;
            color: #33333a;
            text-align: left;
        `,
    },
    PLPdescriptionsText: {
        css: css`
            font-size: 14px;
            letter-spacing: 0;
            line-height: 25px;
            margin-top: 12px;
            text-align: left;
            body {
                font-size: 14px;
                letter-spacing: 0;
                line-height: 25px;
                margin-top: 12px;
                text-align: left;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin-top: 6px;
                        `,
                        css`
                            margin-top: 6px;
                        `,
                        css`
                            margin-top: 6px;
                        `,
                        css`
                            margin-top: 12px;
                        `,
                        css`
                            margin-top: 12px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    subResourceNameLink: {
        css: css`
            width: 100%;
        `,
        color: "text.main",
        typographyProps: {
            css: css`
                width: 100%;
                text-align: center;
            `,
        },
    },

    buttonWrapper: {
        css: css`
            span {
                color: #ffffff;
                font-size: 12px;
                font-weight: bold;
                letter-spacing: 2px;
                margin-top: 25px;
                text-transform: uppercase;
            }
            text-align: center;
            height: 40px;
            width: 180px;
            &:hover {
                background: #000000 !important;
                border-color: #000000 !important;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin-top: 20px;
                        `,
                        css`
                            margin-top: 20px;
                        `,
                        css`
                            margin-top: 20px;
                        `,
                        css`
                            margin-top: 25px;
                        `,
                        css`
                            margin-top: 25px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
};

const styles = newsListViewStyles;

class NewsListView extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            ...this.state,
            isFiltered: false,
            marketsData: this.props.marketsList,
            solutionsData: this.props.solutionsList,
            typesData: this.props.typesList,
            newsData: [],
        };
    }

    UNSAFE_componentWillMount() {
        if (!this.props.pages.value && !this.props.pages.isLoading) {
            this.props.loadPagesForParent(this.props.parameter);
        }
        this.checkQueryString();
    }

    componentDidUpdate() {
        if (!this.props.pages.value && !this.props.pages.isLoading) {
            this.props.loadPagesForParent(this.props.parameter);
        }
        if (!this.props.pages.isLoading && this.props.pages.value && this.state.newsData.length === 0) {
            this.updatePageData();
        }
        this.checkQueryString();
    }
    componentDidMount() {
        this.updatePageData();
    }

    updateMartketFilter = (filteredData: PageProps[]) => {
        const newsListFilter = getNewsFilters();
        const markets = filteredData.map(cat => cat.fields.market);
        const marketsListFiltered = markets.filter(function (elem, pos) {
            // eslint-disable-next-line eqeqeq
            return markets.indexOf(elem) == pos;
        });
        const marketsList = marketsListFiltered.map<FacetModel>(o => ({
            ...o,
            id: o,
            name: o,
            selected: newsListFilter.marketFilters.indexOf(o) !== -1,
        }));
        this.setState({
            marketsData: marketsList,
        });
    };
    updateSolutionsFilter = (filteredData: PageProps[]) => {
        const newsListFilter = getNewsFilters();

        const solutions = filteredData.map(cat => cat.fields.solution);
        const solutionsListFiltered = solutions.filter(function (elem, pos) {
            // eslint-disable-next-line eqeqeq
            return solutions.indexOf(elem) == pos;
        });
        const solutionsList = solutionsListFiltered.map<FacetModel>(o => ({
            ...o,
            id: o,
            name: o,
            selected: newsListFilter.solutionFilters.indexOf(o) !== -1,
        }));
        this.setState({
            solutionsData: solutionsList,
        });
    };
    updateTypeFilter = (filteredData: PageProps[]) => {
        const newsListFilter = getNewsFilters();
        const types = filteredData.map(cat => cat.fields.type);
        const typesListFiltered = types.filter(function (elem, pos) {
            // eslint-disable-next-line eqeqeq
            return types.indexOf(elem) == pos;
        });
        const typesList = typesListFiltered.map<FacetModel>(o => ({
            ...o,
            id: o,
            name: o,
            selected: newsListFilter.typeFilters.indexOf(o) !== -1,
        }));
        this.setState({
            typesData: typesList,
        });
    };

    updatePageData = (_filterType = "onLoad", _isSelected = false) => {
        const {
            pages: { value: pages },
        } = this.props;
        const newsListFilter = getNewsFilters();
        let filteredData: PageProps[];
        if (!pages) {
            return null;
        }
        filteredData = pages;

        if (window.parent.location.href.indexOf("isFiltered=true") !== -1) {
            this.updateMartketFilter(filteredData);
            if (newsListFilter.marketFilters.length !== 0) {
                const filteredMarketData = filteredData.filter(
                    elem => newsListFilter.marketFilters.indexOf(elem.fields.market) !== -1,
                );
                filteredData = filteredMarketData;
                this.updateSolutionsFilter(filteredData);
                this.updateTypeFilter(filteredData);
            }
            this.updateSolutionsFilter(filteredData);
            if (newsListFilter.solutionFilters.length !== 0) {
                const filteredSolutionData = filteredData.filter(
                    elem => newsListFilter.solutionFilters.indexOf(elem.fields.solution) !== -1,
                );
                filteredData = filteredSolutionData;
                this.updateMartketFilter(filteredData);
                this.updateTypeFilter(filteredData);
            }
            this.updateTypeFilter(filteredData);
            if (newsListFilter.typeFilters.length !== 0) {
                const filteredTypeData = filteredData.filter(
                    elem => newsListFilter.typeFilters.indexOf(elem.fields.type) !== -1,
                );
                filteredData = filteredTypeData;
                this.updateMartketFilter(filteredData);
                this.updateSolutionsFilter(filteredData);
            }

            this.setState({
                isFiltered: true,
                newsData: filteredData,
            });
        } else {
            this.props.removeAllNewsFilters();
            this.setState({
                isFiltered: false,
                newsData: filteredData,
            });
            this.updateMartketFilter(filteredData);
            this.updateSolutionsFilter(filteredData);
            this.updateTypeFilter(filteredData);
        }
    };

    changePage = (newPageIndex: number) => {
        this.updateQueryString(newPageIndex, this.props.parameter.pageSize);
        window.scrollTo(0, 0);
    };

    changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        this.updateQueryString(1, newPageSize);
    };

    updateNewsFilter = (_filterType: string, filterList: FacetModel[]) => {
        const newsListFilter = getNewsFilters();
        const filteredList = filterList.map(fil => fil.id);
        if (_filterType === "market") {
            for (const filter of newsListFilter.marketFilters) {
                if (filteredList.indexOf(filter) === -1) {
                    this.props.removeNewsFilters({ filterId: filter, type: _filterType });
                }
            }
        } else if (_filterType === "solution") {
            for (const filter of newsListFilter.solutionFilters) {
                if (filteredList.indexOf(filter) === -1) {
                    this.props.removeNewsFilters({ filterId: filter, type: _filterType });
                }
            }
        } else if (_filterType === "type") {
            for (const filter of newsListFilter.typeFilters) {
                if (filteredList.indexOf(filter) === -1) {
                    this.props.removeNewsFilters({ filterId: filter, type: _filterType });
                }
            }
        }
    };

    updateQueryString(page: number, pageSize?: number) {
        const {
            history,
            location: { pathname },
        } = this.props;
        const { isFiltered } = this.state;
        let newLocation = `${pathname}?page=${page}&isFiltered=${isFiltered}`;
        if (pageSize) {
            newLocation += `&pageSize=${pageSize}`;
        }
        history.push(newLocation);
    }

    checkQueryString() {
        const {
            location: { search },
            parameter: { page, pageSize, parentNodeId },
        } = this.props;
        const parsedQueryString = parseQueryString<{ page?: string; pageSize?: string }>(search);
        const newPage = parsedQueryString.page ? convertToNumberIfString(parsedQueryString.page) : undefined;
        const newPageSize = parsedQueryString.pageSize
            ? convertToNumberIfString(parsedQueryString.pageSize)
            : undefined;
        if (newPage !== page || newPageSize !== pageSize) {
            this.props.updateLoadParameter({
                page: newPage,
                pageSize: newPageSize,
                parentNodeId,
            });
        }
    }

    render() {
        const {
            links,
            pages: { value: pages },
            fields: { defaultPageSize },
            parameter,
        } = this.props;

        if (!pages) {
            return null;
        }

        const totalItems = this.state.newsData.length;
        const page = parameter.page ?? 1;
        const pageSize = parameter.pageSize ?? defaultPageSize;

        const sortedAndPagedPages = sortBy(this.state.newsData, o => o.fields["publishDate"])
            .reverse()
            .slice((page - 1) * pageSize, page * pageSize);

        const onChangeMarket = (facet: FacetModel) => {
            // eslint-disable-next-line eqeqeq
            const objIndex = this.state.marketsData.findIndex(obj => obj.id == facet.id);
            const tempMarketData = [...this.state.marketsData];
            this.updateNewsFilter("market", tempMarketData);
            const market = { ...tempMarketData[objIndex] };
            market.selected = !facet.selected;
            tempMarketData[objIndex] = market;
            if (market.selected) {
                this.props.addNewsFilters({ filterId: market.id, type: "market" });
            } else {
                this.props.removeNewsFilters({ filterId: market.id, type: "market" });
            }
            this.setState({ marketsData: tempMarketData });
            const searchParam = new URLSearchParams(window.location.hash.split("?")[1]);
            searchParam.set("isFiltered", "true");
            window.location.hash = `?${searchParam.toString()}`;
            this.updatePageData("market", market.selected);
        };
        const onChangeSolution = (facet: FacetModel) => {
            // eslint-disable-next-line eqeqeq
            const objIndex = this.state.solutionsData.findIndex(obj => obj.id == facet.id);
            const tempSolutionData = [...this.state.solutionsData];
            this.updateNewsFilter("solution", tempSolutionData);
            const solution = { ...tempSolutionData[objIndex] };
            solution.selected = !facet.selected;
            tempSolutionData[objIndex] = solution;
            if (solution.selected) {
                this.props.addNewsFilters({ filterId: solution.id, type: "solution" });
            } else {
                this.props.removeNewsFilters({ filterId: solution.id, type: "solution" });
            }
            this.setState({ solutionsData: tempSolutionData });
            const searchParam = new URLSearchParams(window.location.hash.split("?")[1]);
            searchParam.set("isFiltered", "true");
            window.location.hash = `?${searchParam.toString()}`;
            this.updatePageData("solution", solution.selected);
        };
        const onChangeType = (facet: FacetModel) => {
            // eslint-disable-next-line eqeqeq
            const objIndex = this.state.typesData.findIndex(obj => obj.id == facet.id);
            const tempTypeData = [...this.state.typesData];
            this.updateNewsFilter("type", tempTypeData);
            const type = { ...tempTypeData[objIndex] };
            type.selected = !facet.selected;
            tempTypeData[objIndex] = type;
            if (type.selected) {
                this.props.addNewsFilters({ filterId: type.id, type: "type" });
            } else {
                this.props.removeNewsFilters({ filterId: type.id, type: "type" });
            }
            this.setState({ typesData: tempTypeData });
            const searchParam = new URLSearchParams(window.location.hash.split("?")[1]);
            searchParam.set("isFiltered", "true");
            window.location.hash = `?${searchParam.toString()}`;
            this.updatePageData("type", type.selected);
        };
        const urlRedirectionfn = (e: React.SyntheticEvent, page: any) =>{
            e.preventDefault();
            const urlValue: string | undefined = links[page.nodeId]?.url || undefined;
            if (urlValue)
            {
                window.location.href = urlValue;
            }
        }
        return (
            <>
                <GridContainer key="NewsListing" {...styles.ResourceContainer}>
                    <GridItem {...styles.ResourceListContainer}>
                        <GridContainer {...styles.container}>
                            <StyledWrapper
                                css={css`
                                    border-bottom: 2px solid rgba(0, 0, 0, 0.1);
                                    width: 100%;
                                `}
                            >
                                <StyledWrapper
                                    css={css`
                                        display: flex;
                                        margin-bottom: 19px;
                                    `}
                                >
                                    <StyledWrapper
                                        css={css`
                                            display: flex;
                                        `}
                                    >
                                        <LazyImage
                                            src="/UserFiles/Images/MiniLogo.jpg"
                                            css={css`
                                                width: 20px;
                                            `}
                                        />
                                    </StyledWrapper>
                                    <StyledWrapper>
                                        <Typography as="h3" {...styles.Rlptitle}>
                                           {translate("Filters")}
                                        </Typography>
                                    </StyledWrapper>
                                </StyledWrapper>
                            </StyledWrapper>
                            <GridItem {...styles.subResourceNameLinkItem} id="marketGrid">
                                <FilterTypeAccordionSection
                                    key="market"
                                    title={translate("Market")}
                                    facets={this.state.marketsData}
                                    onChangeFacet={onChangeMarket}
                                    showMoreLimit={5}
                                    expandByDefault={true}
                                />
                            </GridItem>
                            <GridItem {...styles.subResourceNameLinkItem} id="solutionTypeGrid">
                                <FilterTypeAccordionSection
                                    key="solution"
                                    title={translate("Solution")}
                                    facets={this.state.solutionsData}
                                    onChangeFacet={onChangeSolution}
                                    showMoreLimit={5}
                                    expandByDefault={true}
                                />
                            </GridItem>
                            <GridItem {...styles.subResourceNameLinkItem} id="TYPEGrid">
                                <FilterTypeAccordionSection
                                    key="type"
                                    title={translate("Type")}
                                    facets={this.state.typesData}
                                    onChangeFacet={onChangeType}
                                    showMoreLimit={5}
                                    expandByDefault={true}
                                />
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                    <GridItem {...styles.ResourceListItemContainer}>
                        <StyledWrapper {...styles.listWrapper}>
                            {sortedAndPagedPages.map((page: PageProps) => (
                                <GridContainer {...styles.innerContainer} key={page.id}>
                                    {page.fields["thumbImage"] && (
                                        <GridItem {...styles.subResourceImageItem}>
                                            <Clickable
                                            onClick={(e: React.SyntheticEvent) => urlRedirectionfn(e, page)}
                                                {...styles.subResourceImageClickable}
                                            >
                                                <LazyImage
                                                    src={page.fields["thumbImage"]}
                                                    {...styles.subResourceImage}
                                                />
                                            </Clickable>
                                        </GridItem>
                                    )}
                                    <GridItem {...styles.subResourceNameLinkItem2}>
                                        <StyledWrapper {...styles.subResourceNameLink}>
                                            {page.fields["title"] && (
                                                <Link onClick={(e: React.SyntheticEvent) => urlRedirectionfn(e, page)}>
                                                    <Typography as="h3" {...styles.PLPheaderText}>
                                                        {page.fields["title"]}
                                                    </Typography>
                                                </Link>
                                            )}

                                            {page.fields["newsSummary"] && (
                                                <Typography as="p" {...styles.PLPdescriptionsText}>
                                                    {parse(page.fields["newsSummary"], parserOptions)}
                                                </Typography>
                                            )}
                                            {page.fields["buttonText"] && (
                                                <Link onClick={(e: React.SyntheticEvent) => urlRedirectionfn(e, page)}>
                                                    <Button {...styles.buttonWrapper}>
                                                        {page.fields["buttonText"]}
                                                    </Button>
                                                </Link>
                                            )}
                                        </StyledWrapper>
                                    </GridItem>
                                </GridContainer>
                            ))}
                            {totalItems > defaultPageSize && (
                                <Pagination
                                    resultsCount={totalItems}
                                    currentPage={page}
                                    resultsPerPage={pageSize}
                                    resultsPerPageOptions={[
                                        defaultPageSize,
                                        defaultPageSize * 2,
                                        defaultPageSize * 3,
                                        defaultPageSize * 4,
                                    ]}
                                    onChangePage={this.changePage}
                                    onChangeResultsPerPage={this.changeResultsPerPage}
                                    pageSizeCookie="NewsList-PageSize"
                                    {...styles.pagination}
                                />
                            )}
                        </StyledWrapper>
                    </GridItem>
                </GridContainer>
            </>
        );
    }
}

const widgetModule: WidgetModule = {
    component: withHistory(connect(mapStateToProps, mapDispatchToProps)(NewsListView)),
    definition: {
        group: "News List",
        allowedContexts: ["NewsListPage"],
        fieldDefinitions: [
            {
                name: fields.defaultPageSize,
                editorTemplate: "IntegerField",
                defaultValue: 5,
                fieldType: "General",
                min: 1,
                max: 20,
            },
        ],
    },
};
export default widgetModule;
